import { useTranslation } from 'react-i18next';
import { formatPriceCents } from '../../utils/currency';
import { formatListingTimestamp } from '../../utils/timezone';
import type { EarningsRow } from '../../types/earnings';

// Status chip presentation. `captured-partial` = the cancellation fee was
// kept (order canceled but chef still earned the fee); rendered as
// "Cancelled w/Fee" so the difference from a full cancel is legible at a
// glance. Colors follow the same `text-brand-*` / `border-brand-*` palette
// as ListingHistoryRow.
const STATUS_META: Record<
  EarningsRow['payment']['status'],
  { color: string; labelKey: string }
> = {
  captured: { color: 'success', labelKey: 'earnings.status.captured' },
  cancelled: { color: 'medium', labelKey: 'earnings.status.cancelled' },
  'captured-partial': {
    color: 'warning',
    labelKey: 'earnings.status.capturedPartial',
  },
};

interface EarningsHistoryRowProps {
  row: EarningsRow;
  onClick: () => void;
}

/**
 * Compact horizontal row for the My Earnings Pending feed. Same visual
 * language as ListingHistoryRow (image + title + status chip over a meta
 * line) so the two pages read as a family.
 *
 * Row is a single earnings event, keyed by payment.id (order ↔ payment is
 * 1:1 in the model today). Line 1 = orderNumber · $gross · payment.updatedAt
 * in the listing's timezone (per product decision — chef sees pickup context
 * in the pickup's own clock). Line 2 is the settlement/payout state, tinted
 * text-brand-primary, driven by the payment status + snapshot fields:
 *   - cancelled           → "Order Cancelled"
 *   - captured-partial    → "Cancelled Fee Captured"
 *   - captured, no_show   → "No-Show Fee Captured"
 *   - captured, sale
 *      - no lister_balance_id  → "Pending Settlement"
 *      - has lister_balance_id → "Applied to Balance"
 *
 * Two states, not three. Earnings now land in a balance the chef draws against
 * rather than being paid out per order, so "has this order been paid out" is no
 * longer a question the row can answer — a payout covers an amount, not a set
 * of orders. The boundary is the balance credit, which is written when the
 * Stripe payout carrying the order reconciles.
 */
export const EarningsHistoryRow: React.FC<EarningsHistoryRowProps> = ({ row, onClick }) => {
  const { t } = useTranslation('profile');
  const { order, listing, payment, snapshot } = row;
  const meta = STATUS_META[payment.status];

  const grossPart =
    snapshot?.grossCents != null
      ? formatPriceCents(snapshot.grossCents)
      : null;
  // Drop the year on the earnings list — retention is short enough that the
  // month/day alone is unambiguous, and the compact form keeps the meta line
  // from wrapping on smaller phones.
  const stampPart = formatListingTimestamp(payment.updatedAt, listing.timezone, {
    year: false,
  });
  const metaParts = [order.orderNumber, grossPart, stampPart].filter(Boolean);

  const state = deriveStateLine(row);

  return (
    // <button> matches ListingHistoryRow so the two rows behave identically
    // (hover, tap, keyboard). Multiple earnings rows for the same listing all
    // navigate to the same listing detail page — that's expected.
    <button
      type="button"
      onClick={onClick}
      className="hp-history-row block w-full p-3 text-left hover:bg-surface-100"
    >
      <div className="hp-history-row__inner">
        <div
          className="hp-history-row__thumb shrink-0 overflow-hidden rounded-lg"
          style={{
            // Inline size (see ListingHistoryRow — same rationale). Kept in
            // lockstep with that row so the two pages read as a family.
            width: '3.25rem',
            height: '3.25rem',
            backgroundColor: 'var(--ion-color-light)',
          }}
        >
          {listing.thumbnailUrl || listing.imageUrl ? (
            // object-contain — see ListingHistoryRow for rationale. Kept in
            // lockstep so the two pages read as a family.
            <img
              src={listing.thumbnailUrl ?? listing.imageUrl ?? ''}
              alt={listing.title}
              className="h-full w-full object-contain"
            />
          ) : null}
        </div>

        <div className="hp-history-row__body min-w-0 flex-1">
          <div className="hp-history-row__head">
            <h3 className="hp-history-row__title font-medium">{listing.title}</h3>
            <span
              className={`hp-history-row__badge shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium border-brand-${meta.color} text-brand-${meta.color}`}
            >
              {t(meta.labelKey)}
            </span>
          </div>
          <p className="hp-history-row__meta mt-1 text-xs text-muted-foreground">
            {metaParts.join(' · ')}
          </p>
          {state && (
            <p className={`mt-0.5 text-xs ${state.colorClass}`}>
              {t(state.key)}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

// Kept module-local rather than moved onto the type — the mapping is purely
// display-side and depends on i18n keys that don't belong on the wire model.
//
// Color coding follows the settlement lifecycle:
//   - Pending Settlement  → brand-secondary (dark blue): funds authorized but
//     not yet credited — either still with the processor, or settled but on a
//     payout that has not reconciled.
//   - Applied to Balance  → brand-success (dark green): credited, and the
//     chef's to draw on.
//   - Everything else (cancels, captured-fee states) → brand-primary — the
//     terminal / informational states that share the app's default accent.
function deriveStateLine(row: EarningsRow): { key: string; colorClass: string } | null {
  const { payment, snapshot } = row;
  if (payment.status === 'cancelled') {
    return { key: 'earnings.state.orderCancelled', colorClass: 'text-brand-primary' };
  }
  if (payment.status === 'captured-partial') {
    return { key: 'earnings.state.cancelledFeeCaptured', colorClass: 'text-brand-primary' };
  }
  if (payment.status === 'captured') {
    if (snapshot?.transactionType === 'no_show') {
      return { key: 'earnings.state.noShowFeeCaptured', colorClass: 'text-brand-primary' };
    }
    if (!snapshot?.listerBalanceId) {
      return { key: 'earnings.state.pendingSettlement', colorClass: 'text-brand-secondary' };
    }
    return { key: 'earnings.state.appliedToBalance', colorClass: 'text-brand-success' };
  }
  return null;
}
